import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  IFilter,
  IQueryParam,
  IReporteGenerico,
  IReporteTrackerCombustible,
  TipoValoresReporte,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../auxiliares/servicios/helper.service';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../auxiliares/tabla/filtro/filtro.component';
import { IColumnas } from '../../auxiliares/tabla/tabla.component';
import { estadoHtml } from '../../auxiliares/tabla/celdas';
import { escaparHtml } from '../../auxiliares/html-seguro.util';
import { AuxiliaresModule } from '../../auxiliares/auxiliares.module';
import { Lista2Service } from '../../auxiliares/servicios/lista2.service';
import { FormularioDialogService } from '../../auxiliares/servicios/formulario-dialog.service';
import { MapaFacilDialogComponent } from '../../auxiliares/mapa-facil-dialog/mapa-facil-dialog.component';
import { MapaFacilData } from '../../auxiliares/mapa-facil/interfaces';

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-reportes-combustible',
  templateUrl: './listado-reportes-combustible.component.html',
  styleUrl: './listado-reportes-combustible.component.scss',
})
export class ListadoReportesCombustibleComponent
  implements OnInit, OnDestroy, OnChanges
{
  private lista2 = inject(Lista2Service);
  private formularios = inject(FormularioDialogService);
  helper = inject(HelperService);

  @Input() public idTracker?: string;
  @Input() public idActivo?: string;

  public name = 'ListadoReportesCombustibleComponent';
  public reportes?: IReporteGenerico[];
  public reportes$?: Subscription;
  public totalCount = 0;
  public columnas: IColumnas<IReporteGenerico>[];

  public search: IRegExpSearch = { fields: [] };

  private variacion: IFiltroTabla = {
    filter: { field: 'valores.variacionCombustible' },
    label: 'Tipo de evento',
    tipo: 'select',
    rapido: true,
    elementos: [
      { label: 'Cargas', value: { $gt: 0 } },
      { label: 'Descargas', value: { $lt: 0 } },
      { label: 'Cargas y descargas', value: { $exists: true } },
    ],
    selectLabel: 'label',
    selectValue: 'value',
    // Los values son objetos ({$gt: 0}...): al restaurarse desde
    // sessionStorage pierden la referencia y hay que comparar por JSON.
    compararPorJson: true,
  };

  // Se filtra por valores.timestamp (hora del evento) y NO por fechaCreacion:
  // es el único campo de fecha que entra en el índice
  // {idsAsignados, tipoReporte, valores.timestamp} de reportegenericos.
  private fecha: IFiltroTabla = {
    filter: { field: 'valores.timestamp' },
    label: 'Fecha',
    tipo: 'dateRange',
  };

  public cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };

  public filtros: IFiltroTabla[] = [
    this.variacion,
    this.fecha,
    this.cliente,
    this.ancestro,
  ];

  // sort por valores.timestamp: con -fechaCreacion (sin índice útil para este
  // filtro) el plan escaneaba fechaCreacion_1 entero buscando las variaciones,
  // que son ~1 en mil reportes, y moría por maxTimeMS (45s) => listado vacío.
  public query: IQueryParam = {
    sort: '-valores.timestamp',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    excludeTotalCount: true,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    const filter: IFilter<IReporteGenerico> = JSON.parse(
      this.query.filter || '{}',
    );

    if (this.idTracker) filter.idEntidad = this.idTracker;
    if (this.idActivo) filter.idsAsignados = this.idActivo;

    this.query.filter = JSON.stringify(filter);

    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .reportesGenericos(
        ['Tracker 4G Combustible'] as TipoValoresReporte[],
        this.query,
        30,
      )
      .subscribe((data) => {
        const limit = +(this.query.limit || 10);
        const page = +(this.query.page || 0);
        const datos = data.datos || [];
        // El +1 fuerza el refresco del paginado, ya que el backend no devuelve totalCount
        this.totalCount =
          datos.length === limit
            ? (page + 1) * limit + 1
            : page * limit + datos.length;
        this.reportes = datos;
      });
  }

  public verUbicacion(reporte: IReporteGenerico): void {
    const coords = (reporte.valores as IReporteTrackerCombustible)?.geojson
      ?.coordinates;
    if (!coords || coords.length < 2) return;
    const coord: [number, number] = [coords[0], coords[1]];
    const data: MapaFacilData = {
      markers: [{ coor: coord, label: 'puntoSimple' }],
      satelliteView: true,
    };
    this.formularios.abrir(MapaFacilDialogComponent, {
      tamano: 'grande',
      data,
      cerrarAfuera: true,
    });
  }

  /** Litros con separador de miles y la unidad en la celda. */
  private litros(n?: number | null): string | undefined {
    if (n === undefined || n === null) return undefined;
    return `${n.toLocaleString('es-AR', { maximumFractionDigits: 2 })} L`;
  }

  private setColumnas(): void {
    const valores = (dato: IReporteGenerico) =>
      dato.valores as IReporteTrackerCombustible;
    this.columnas = [
      {
        header: { label: 'Fecha', sortable: true, sort: 'valores.timestamp' },
        row: {
          // Hora del evento; fechaCreacion como fallback para reportes
          // viejos sin timestamp.
          fecha: (dato) => dato.valores?.timestamp ?? dato.fechaCreacion,
          hace: true,
          noData: 'Sin fecha',
        },
      },
      {
        header: { label: 'Total' },
        row: {
          parse: (dato) => this.litros(valores(dato)?.nivelCombustibleTotal),
          noData: 'Sin dato',
        },
      },
      {
        header: { label: 'Sensor 1' },
        row: {
          parse: (dato) => this.litros(valores(dato)?.nivelCombustible1),
          noData: 'Sin sensor',
        },
      },
      {
        header: { label: 'Sensor 2' },
        row: {
          parse: (dato) => this.litros(valores(dato)?.nivelCombustible2),
          noData: 'Sin sensor',
        },
      },
      {
        header: { label: 'Sensor 3' },
        row: {
          parse: (dato) => this.litros(valores(dato)?.nivelCombustible3),
          noData: 'Sin sensor',
        },
      },
      {
        header: { label: 'Sensor 4' },
        row: {
          parse: (dato) => this.litros(valores(dato)?.nivelCombustible4),
          noData: 'Sin sensor',
        },
      },
      {
        header: { label: 'Variación' },
        row: {
          // Carga en verde y descarga en rojo, ahora con el nombre además del
          // color. Una descarga que gestion-cron descartó al contrastarla con
          // el nivel dice «Falsa alarma» y el motivo va en el tooltip.
          html: (dato) => {
            const v = valores(dato);
            const variacion = v?.variacionCombustible;
            if (variacion === undefined || variacion === null) return '';
            const carga = variacion > 0;
            const texto = `${carga ? '+' : ''}${this.litros(variacion)}`;
            let html = `<div class="tabla-celda-col"><span>${estadoHtml({
              texto,
              tono: carga ? 'ok' : 'error',
            })}</span>`;
            const detalle = [carga ? 'Carga' : 'Descarga'];
            if (v?.validacionDescarga?.estado === 'falsaAlarma') {
              detalle.push('Falsa alarma');
            }
            html += `<span class="tabla-secundario">${detalle.join(' · ')}</span></div>`;
            return html;
          },
          tooltip: (dato) => {
            const validacion = valores(dato)?.validacionDescarga;
            return validacion?.estado === 'falsaAlarma'
              ? escaparHtml(
                  validacion.motivo
                    ? `Falsa alarma: ${validacion.motivo}`
                    : 'Falsa alarma',
                )
              : undefined;
          },
          noData: 'Sin variación',
        },
      },
      {
        header: { label: 'Post-variación' },
        row: {
          parse: (dato) => this.litros(valores(dato)?.nivelPostVariacion),
          noData: 'Sin dato',
        },
      },
      {
        header: { label: 'Temperatura' },
        row: {
          parse: (dato) => {
            const t = valores(dato)?.temperaturaCombustible;
            return t === undefined || t === null
              ? undefined
              : `${t.toLocaleString('es-AR', { maximumFractionDigits: 1 })} °C`;
          },
          noData: 'Sin dato',
        },
      },
      {
        header: { label: 'Alarma de agua' },
        row: {
          estado: (dato) => {
            const v = valores(dato);
            if (v?.alarmaAgua === undefined || v?.alarmaAgua === null)
              return undefined;
            return v.alarmaAgua
              ? {
                  texto: 'Detectada',
                  tono: 'aviso',
                  icono: 'warning',
                  tooltip: 'Alarma de agua detectada',
                }
              : { texto: 'Sin alarma', tono: 'ok', icono: 'check_circle' };
          },
          noData: 'Sin dato',
        },
      },
      {
        header: { label: 'Ignición' },
        row: {
          // Mismos íconos que el listado de vehículos (key / key_off)
          estado: (dato) => {
            const v = valores(dato);
            if (
              v?.ignicionVirtualSensor === undefined ||
              v?.ignicionVirtualSensor === null
            )
              return undefined;
            return v.ignicionVirtualSensor
              ? {
                  texto: 'Encendido',
                  tono: 'ok',
                  icono: 'key',
                  tooltip: 'Motor encendido',
                }
              : {
                  texto: 'Apagado',
                  tono: 'neutro',
                  icono: 'key_off',
                  tooltip: 'Motor apagado',
                };
          },
          noData: 'Sin dato',
        },
      },
      {
        header: { label: 'Ubicación' },
        row: {
          field: 'acciones',
          acciones: [
            {
              tipo: 'material',
              icon: 'map',
              tooltip: 'Ver en el mapa',
              color: 'primary',
              click: (dato) => this.verUbicacion(dato),
              oculta: (dato) => !valores(dato)?.geojson?.coordinates,
            },
          ],
        },
      },
    ];
  }

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  async ngOnChanges() {
    this.setColumnas();
    await this.listar();
  }

  async ngOnDestroy() {
    this.reportes$?.unsubscribe();
  }
}
